import { ApiError } from "@aurumiq/api-client";
import { toast } from "@aurumiq/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, qs, rawApi } from "@/lib/api";
import type {
  ActivityItem,
  BillingPlan,
  Device,
  License,
  MemberInviteInput,
  MemberInviteResult,
  MemberRole,
  OrgCreateInput,
  OrgDetail,
  OrgMember,
  OrgMetrics,
  OrgPatchInput,
  OrgSummary,
  OrgTelemetry,
  Paginated,
  Subscription,
  SubscriptionPutInput,
} from "@/lib/types";

const BASE = "/api/v1/admin";

export const orgKeys = {
  all: ["admin", "orgs"] as const,
  detail: (id: string) => ["admin", "orgs", id] as const,
  sub: (id: string, res: string, extra?: unknown) =>
    extra === undefined
      ? (["admin", "orgs", id, res] as const)
      : (["admin", "orgs", id, res, extra] as const),
  plans: ["admin", "billing", "plans"] as const,
};

export function useOrg(id: string) {
  return useQuery({
    queryKey: orgKeys.detail(id),
    queryFn: () => rawApi.get<OrgDetail>(`${BASE}/orgs/${id}`),
  });
}

export function useOrgMembers(id: string) {
  return useQuery({
    queryKey: orgKeys.sub(id, "members"),
    queryFn: () => rawApi.get<Paginated<OrgMember>>(`${BASE}/orgs/${id}/members`),
  });
}

export function useOrgSubscription(id: string) {
  return useQuery({
    queryKey: orgKeys.sub(id, "subscription"),
    queryFn: () => rawApi.get<Subscription | null>(`${BASE}/orgs/${id}/subscription`),
  });
}

const FALLBACK_PLANS: BillingPlan[] = ["starter", "pro", "enterprise"].map((code) => ({
  code,
  name: code[0]?.toUpperCase() + code.slice(1),
  tier: code,
  seats: 0,
  price_month: 0,
  price_year: 0,
  currency: "USD",
  features: [],
}));

/** Plans from billing; falls back to the three known codes when the endpoint is missing. */
export function usePlans() {
  return useQuery({
    queryKey: orgKeys.plans,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async () => {
      try {
        const r = await rawApi.get<{ items: BillingPlan[] }>(`${BASE}/billing/plans`);
        return { items: r.items, fallback: false };
      } catch (err) {
        if (err instanceof ApiError && (err.isNotFound || err.isNetwork))
          return { items: FALLBACK_PLANS, fallback: true };
        throw err;
      }
    },
  });
}

export function useOrgLicenses(id: string, page = 1) {
  return useQuery({
    queryKey: orgKeys.sub(id, "licenses", page),
    queryFn: () => rawApi.get<Paginated<License>>(`${BASE}/orgs/${id}/licenses${qs({ page })}`),
  });
}

export function useOrgDevices(id: string) {
  return useQuery({
    queryKey: orgKeys.sub(id, "devices"),
    queryFn: () => rawApi.get<Paginated<Device>>(`${BASE}/orgs/${id}/devices`),
  });
}

export function useOrgActivity(id: string, page = 1) {
  return useQuery({
    queryKey: orgKeys.sub(id, "activity", page),
    queryFn: () =>
      rawApi.get<Paginated<ActivityItem>>(`${BASE}/orgs/${id}/activity${qs({ page })}`),
  });
}

export function useOrgMetrics(id: string) {
  return useQuery({
    queryKey: orgKeys.sub(id, "metrics"),
    queryFn: () => rawApi.get<OrgMetrics>(`${BASE}/orgs/${id}/metrics`),
  });
}

export function useOrgTelemetry(id: string) {
  return useQuery({
    queryKey: orgKeys.sub(id, "telemetry"),
    queryFn: () => rawApi.get<OrgTelemetry>(`${BASE}/orgs/${id}/telemetry`),
  });
}

/* ---------- mutations: every one invalidates the org subtree and toasts ---------- */

function useOrgMutation<TVars, TData = unknown>(
  id: string,
  fn: (vars: TVars) => Promise<TData>,
  success: string | ((data: TData, vars: TVars) => string | undefined),
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: orgKeys.detail(id) });
      qc.invalidateQueries({ queryKey: orgKeys.all, exact: true });
      const msg = typeof success === "function" ? success(data, vars) : success;
      if (msg) toast.success(msg);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrgCreateInput) => rawApi.post<OrgDetail>(`${BASE}/orgs`, input),
    onSuccess: (org) => {
      qc.invalidateQueries({ queryKey: orgKeys.all });
      toast.success(`${org.name} created`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

export function usePatchOrg(id: string) {
  return useOrgMutation(
    id,
    (input: OrgPatchInput) => rawApi.patch<OrgDetail>(`${BASE}/orgs/${id}`, input),
    "Organisation updated",
  );
}

export function useInviteMember(id: string) {
  return useOrgMutation(
    id,
    (input: MemberInviteInput) =>
      rawApi.post<MemberInviteResult>(`${BASE}/orgs/${id}/members`, input),
    (r) => `${r.member.email} invited`,
  );
}

export function usePatchMember(id: string) {
  return useOrgMutation(
    id,
    ({ mid, role, reason }: { mid: string; role: MemberRole; reason?: string }) =>
      rawApi.patch<OrgMember>(`${BASE}/orgs/${id}/members/${mid}`, { role, reason }),
    "Role updated",
  );
}

export function useRemoveMember(id: string) {
  return useOrgMutation(
    id,
    ({ mid, reason }: { mid: string; reason: string }) =>
      rawApi.delete<void>(`${BASE}/orgs/${id}/members/${mid}${qs({ reason })}`),
    "Member removed",
  );
}

export type ImpersonateTarget = "portal" | "workstation";

/** Superadmin support access: a one-time sign-in link as this member, to the customer portal
 * or to the organisation's cloud workstation. */
export function useImpersonateMember(id: string) {
  return useMutation({
    mutationFn: ({ mid, target = "portal" }: { mid: string; target?: ImpersonateTarget }) =>
      rawApi.post<{ url: string; email: string; expires_in_s: number }>(
        `${BASE}/orgs/${id}/members/${mid}/impersonate?target=${target}`,
      ),
    onError: (err) => toast.error(errorMessage(err)),
  });
}

/**
 * Open a new tab signed in as a member. The tab is opened synchronously (popup blockers) and
 * its opener dropped so the destination cannot reach back into the admin panel.
 */
export function openAsMember(
  impersonate: ReturnType<typeof useImpersonateMember>,
  mid: string,
  target: ImpersonateTarget,
): void {
  const tab = window.open("", "_blank");
  if (tab) tab.opener = null;
  impersonate.mutate(
    { mid, target },
    {
      onSuccess: (r) => {
        if (tab) tab.location.href = r.url;
        else window.open(r.url, "_blank");
      },
      onError: () => tab?.close(),
    },
  );
}

export const DEMO_ORG_CODE = "DEMO01";

/** The demo organisation (seeded as DEMO01) with its members, for the dashboard shortcut. */
export function useDemoOrg() {
  return useQuery({
    queryKey: ["admin", "orgs", "demo"] as const,
    queryFn: async () => {
      const orgs = await rawApi.get<Paginated<OrgSummary>>(
        `${BASE}/orgs${qs({ q: DEMO_ORG_CODE, page_size: 5 })}`,
      );
      const org = orgs.items.find((o) => o.code === DEMO_ORG_CODE) ?? null;
      if (!org) return null;
      const members = await rawApi.get<Paginated<OrgMember>>(`${BASE}/orgs/${org.id}/members`);
      return { org, members: members.items };
    },
    staleTime: 60_000,
  });
}

export function usePutSubscription(id: string) {
  return useOrgMutation(
    id,
    (input: SubscriptionPutInput) =>
      rawApi.put<Subscription>(`${BASE}/orgs/${id}/subscription`, input),
    "Subscription updated",
  );
}

export function useRevokeLicense(orgId: string) {
  return useOrgMutation(
    orgId,
    ({ id, reason }: { id: string; reason: string }) =>
      rawApi.post<unknown>(`${BASE}/licenses/${id}/revoke`, { reason }),
    "Licence revoked",
  );
}

export function useRevokeDevice(orgId: string) {
  return useOrgMutation(
    orgId,
    ({ did, reason }: { did: string; reason: string }) =>
      rawApi.post<unknown>(`${BASE}/orgs/${orgId}/devices/${did}/revoke`, { reason }),
    "Device revoked",
  );
}
